import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useRef,
  useEffect,
} from 'react'
import { InnerImageZoomRef } from 'react-inner-image-zoom'

interface ImageTransformContextType {
  rotation: number
  scale: number
  getZoomScaleForSrc: (src: string) => number
  setRotation: (rotation: number) => void
  setScale: (scale: number) => void
  rotate90: () => void
  zoomIn: () => void
  zoomOut: () => void
  reset: () => void
  resetRotation: () => void
  setImageRef: (element: HTMLImageElement | null) => void
  scaleChangeEnabled: boolean
  setScaleChangeEnabled: (enabled: boolean) => void
  scaleOptions: number[]
  // для установки ref в react-inner-image-zoom
  setZoomImageRef: (ref: InnerImageZoomRef) => void
}

const ImageTransformContext = createContext<
  ImageTransformContextType | undefined
>(undefined)

const useImageTransform = () => {
  const context = useContext(ImageTransformContext)
  if (!context) {
    throw new Error(
      'useImageTransform must be used within an ImageTransformProvider'
    )
  }
  return context
}

interface ImageTransformProviderProps {
  children: React.ReactNode
  initialRotation?: number
  initialScale?: number
  minScale?: number
  maxScale?: number
  scaleStep?: number
  scaleChangeEnabled?: boolean
  scaleOptions?: number[]
}

const calculateRotationScale = (
  image: HTMLImageElement,
  rotation: number
): number => {
  const container = image.parentElement
  if (!container) return 1

  if (!image.naturalWidth || !image.naturalHeight) return 1

  const containerRect = container.getBoundingClientRect()
  const originalWidth = image.naturalWidth
  const originalHeight = image.naturalHeight
  const containerWidth = containerRect.width
  const containerHeight = containerRect.height

  const normalScale = Math.min(
    containerWidth / originalWidth,
    containerHeight / originalHeight
  )

  const effectiveRotation = rotation % 360

  if (effectiveRotation === 90 || effectiveRotation === 270) {
    const rotatedScale = Math.min(
      containerWidth / originalHeight,
      containerHeight / originalWidth
    )
    return rotatedScale / normalScale
  }

  return 1
}

const normalizeRotation = (rotation: number) => {
  const normalized = rotation % 360
  return normalized < 0 ? normalized + 360 : normalized
}

const ImageTransformProvider: React.FC<ImageTransformProviderProps> = ({
  children,
  initialRotation = 0,
  initialScale = 3,
  minScale = 0.5,
  maxScale = 4,
  scaleStep = 0.5,
  scaleChangeEnabled: initialScaleChangeEnabled = false,
  scaleOptions = [2, 2.5, 3, 4],
}) => {
  const [rotation, setRotationState] = useState(initialRotation)
  const [scale, setScaleState] = useState(initialScale)
  const [scaleChangeEnabled, setScaleChangeEnabled] = useState(
    initialScaleChangeEnabled
  )
  const [currentZoomFit, setCurrentZoomFit] = useState(1)
  const zoomFitMapRef = useRef<Map<string, number>>(new Map())
  const imageRefs = useRef<Array<HTMLImageElement>>([])
  const idCounter = useRef(0)
  const rotationRef = useRef(initialRotation)
  const zoomBaseRef = useRef<HTMLImageElement | null>(null)
  const zoomCleanupRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    return () => {
      zoomCleanupRef.current?.()
    }
  }, [])

  const recalculateZoomFit = useCallback((src: string) => {
    const el = zoomBaseRef.current
    if (!el || !src) return

    const { width, height } = el.getBoundingClientRect()
    const naturalWidth = el.naturalWidth
    const naturalHeight = el.naturalHeight

    if (!width || !height || !naturalWidth || !naturalHeight) {
      zoomFitMapRef.current.set(src, 1)
      setCurrentZoomFit(1)
      return
    }

    const fit = Math.min(width / naturalWidth, height / naturalHeight)
    const finalFit = Number.isFinite(fit) && fit > 0 ? fit : 1
    zoomFitMapRef.current.set(src, finalFit)
    setCurrentZoomFit(finalFit)
  }, [])

  const applyTransformToElement = useCallback(
    (element: HTMLImageElement, rot: number) => {
      if (element.dataset.skipTransform === 'true') return
      const rotationScale = calculateRotationScale(element, rot)
      element.style.transform = `rotate(${rot}deg) scale(${rotationScale})`
      element.style.transformOrigin = 'center'
    },
    []
  )

  const applyTransform = useCallback(
    (newRotation: number) => {
      imageRefs.current.forEach(image => {
        applyTransformToElement(image, newRotation)
      })
    },
    [applyTransformToElement]
  )

  const setImageRef = useCallback(
    (element: HTMLImageElement | null) => {
      if (element) {
        if (!element.dataset.imageId) {
          element.dataset.imageId = `image-${idCounter.current++}`
        }

        const isDuplicate = imageRefs.current.some(
          existingElement =>
            existingElement.dataset.imageId === element.dataset.imageId
        )

        if (!isDuplicate) {
          imageRefs.current.push(element)
          element.style.transition = 'transform 0.3s ease'
          applyTransformToElement(element, rotationRef.current)
        }
      }
    },
    [applyTransformToElement]
  )

  const setRotation = useCallback(
    (newRotation: number) => {
      const normalizedRotation = normalizeRotation(newRotation)
      rotationRef.current = normalizedRotation
      setRotationState(normalizedRotation)
      applyTransform(normalizedRotation)
    },
    [applyTransform]
  )

  const setScale = useCallback(
    (newScale: number) => {
      const clampedScale = Math.min(Math.max(newScale, minScale), maxScale)
      setScaleState(clampedScale)
    },
    [minScale, maxScale]
  )

  const rotate90 = useCallback(() => {
    const newRotation = normalizeRotation(rotationRef.current + 90)
    rotationRef.current = newRotation
    setRotationState(newRotation)
    applyTransform(newRotation)
  }, [applyTransform])

  const zoomIn = useCallback(() => {
    const newScale = Math.min(scale + scaleStep, maxScale)
    setScaleState(newScale)
  }, [scale, scaleStep, maxScale])

  const zoomOut = useCallback(() => {
    const newScale = Math.max(scale - scaleStep, minScale)
    setScaleState(newScale)
  }, [scale, scaleStep, minScale])

  const reset = useCallback(() => {
    rotationRef.current = initialRotation
    setRotationState(initialRotation)
    setScaleState(initialScale)
    applyTransform(initialRotation)
  }, [initialRotation, initialScale, applyTransform])

  const resetRotation = useCallback(() => {
    rotationRef.current = initialRotation
    setRotationState(initialRotation)
    applyTransform(initialRotation)
  }, [initialRotation, applyTransform])

  const setZoomImageRef = useCallback(
    (ref: InnerImageZoomRef) => {
      if (!ref?.container) return

      const imageElement = ref.container?.querySelector(
        '.iiz__img'
      ) as HTMLImageElement
      const imageElementZoom = ref.container?.querySelector(
        '.iiz__zoom-img'
      ) as HTMLImageElement

      if (imageElement) {
        const syncImageTransformMode = () => {
          const wasSkippingTransform =
            imageElement.dataset.skipTransform === 'true'
          const imageSrc = imageElement.currentSrc || imageElement.src
          const shouldSkipTransform = imageSrc.startsWith('data:')
          imageElement.dataset.skipTransform = shouldSkipTransform
            ? 'true'
            : 'false'

          if (shouldSkipTransform) {
            imageElement.style.transform = ''
          } else {
            const rotationToApply = wasSkippingTransform
              ? normalizeRotation(rotationRef.current)
              : rotationRef.current
            applyTransformToElement(imageElement, rotationToApply)
          }
        }

        syncImageTransformMode()
        setImageRef(imageElement)
        zoomBaseRef.current = imageElement
        const src = imageElement.src

        // Сразу вычисляем zoomFit при установке ref
        if (imageElement.complete) {
          recalculateZoomFit(src)
        } else {
          imageElement.addEventListener('load', () => recalculateZoomFit(src), {
            once: true,
          })
        }

        // И при mouseenter (для переключения между изображениями)
        const handleMouseEnter = () => {
          zoomBaseRef.current = imageElement
          recalculateZoomFit(src)
        }
        ref.container.addEventListener('mouseenter', handleMouseEnter)
        imageElement.addEventListener('load', syncImageTransformMode)
      }
      if (imageElementZoom) {
        const syncZoomTransformMode = () => {
          const wasSkippingTransform =
            imageElementZoom.dataset.skipTransform === 'true'
          const zoomSrc = imageElementZoom.currentSrc || imageElementZoom.src
          const shouldSkipTransform = zoomSrc.startsWith('data:')
          imageElementZoom.dataset.skipTransform = shouldSkipTransform
            ? 'true'
            : 'false'

          if (shouldSkipTransform) {
            imageElementZoom.style.transform = ''
          } else {
            const rotationToApply = wasSkippingTransform
              ? normalizeRotation(rotationRef.current)
              : rotationRef.current
            applyTransformToElement(imageElementZoom, rotationToApply)
          }
        }

        syncZoomTransformMode()
        setImageRef(imageElementZoom)
        imageElementZoom.addEventListener('load', syncZoomTransformMode)
      }
    },
    [setImageRef, recalculateZoomFit, applyTransformToElement]
  )

  const getZoomScaleForSrc = useCallback(
    (src: string) => {
      const zoomFit = zoomFitMapRef.current.get(src) ?? currentZoomFit
      // Для сжатых изображений (zoomFit < 1): zoomScale = scale * zoomFit (относительно отображаемого)
      // Для растянутых изображений (zoomFit >= 1): zoomScale = scale (относительно натурального)
      return zoomFit < 1 ? scale * zoomFit : scale
    },
    [scale, currentZoomFit]
  )

  const contextValue: ImageTransformContextType = {
    rotation,
    scale,
    getZoomScaleForSrc,
    setRotation,
    setScale,
    rotate90,
    zoomIn,
    zoomOut,
    reset,
    resetRotation,
    setImageRef,
    scaleChangeEnabled,
    setScaleChangeEnabled,
    scaleOptions,
    setZoomImageRef,
  }

  return (
    <ImageTransformContext.Provider value={contextValue}>
      {children}
    </ImageTransformContext.Provider>
  )
}

export { ImageTransformProvider, useImageTransform }
export type { ImageTransformContextType, ImageTransformProviderProps }
